import { CheckCircle, XCircle, Clock, HelpCircle, Loader2 } from 'lucide-react';

export type VerificationStatus = 'verified' | 'failed' | 'pending' | 'never_checked';

interface SlugVerificationBadgeProps {
  status: VerificationStatus;
  verifiedAt?: string | null;
  showLabel?: boolean;
  size?: 'sm' | 'md';
  isLoading?: boolean;
}

export function SlugVerificationBadge({
  status,
  verifiedAt,
  showLabel = true,
  size = 'sm',
  isLoading = false,
}: SlugVerificationBadgeProps) {
  if (isLoading) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-600">
        <Loader2 size={size === 'sm' ? 12 : 14} className="animate-spin" />
        {showLabel && 'Verifying...'}
      </span>
    );
  }

  const config = {
    verified: {
      icon: CheckCircle,
      color: 'text-green-600',
      bg: 'bg-green-100',
      label: 'Verified',
    },
    failed: {
      icon: XCircle,
      color: 'text-red-600',
      bg: 'bg-red-100',
      label: 'Failed',
    },
    pending: {
      icon: Clock,
      color: 'text-yellow-600',
      bg: 'bg-yellow-100',
      label: 'Pending',
    },
    never_checked: {
      icon: HelpCircle,
      color: 'text-gray-500',
      bg: 'bg-gray-100',
      label: 'Not Verified',
    },
  };

  const { icon: Icon, color, bg, label } = config[status];
  const iconSize = size === 'sm' ? 12 : 14;

  const badge = (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${bg} ${color}`}>
      <Icon size={iconSize} />
      {showLabel && label}
    </span>
  );

  // Add tooltip with verification date if available
  if (verifiedAt) {
    const date = new Date(verifiedAt);
    const formattedDate = date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    return (
      <span title={`Last verified: ${formattedDate}`}>
        {badge}
      </span>
    );
  }

  return badge;
}
